import { OPTION_TYPE } from '../../constant/product-option';
import {
  InitialSelectedOption,
  ItemCardState,
  ItemListMapValueType,
  OptionInfo,
  ProductOptionConfig,
} from '../../type/common.type';
import { productMetadataBuilder } from '../../util/transformers/product-metadata-transformer';
import { buildItemCard } from '../item-card.core';

/**
 * 초기 선택 옵션 정보를 바탕으로 아이템 카드를 생성합니다.
 */
export const createInitialItemCardPayload = ({
  initialOption,
  optionInfo,
  itemCards,
  productMetadata,
  config,
}: {
  initialOption: InitialSelectedOption;
  optionInfo: OptionInfo;
  itemCards: ItemCardState;
  productMetadata: ReturnType<typeof productMetadataBuilder>;
  config?: ProductOptionConfig;
}): ItemCardState | null => {
  const { product_item_id, option_type, quantity, custom_input_option_list, bogo_product_item_id_list } = initialOption;

  // 1. 메인 아이템 찾기
  const foundItem = findItemById(optionInfo, product_item_id, option_type);

  if (!foundItem) {
    return null;
  }

  // 2. BOGO 아이템 찾기
  const bogoItems =
    (bogo_product_item_id_list
      ?.map((id) => findItemById(optionInfo, id, 'BASIC'))
      .filter(Boolean) as ItemListMapValueType[]) ?? [];

  // 3. 커스텀 입력 옵션 변환
  const customInputList =
    custom_input_option_list?.map((detail) => ({
      [detail.name]: detail.value,
    })) ?? [];

  const context = {
    bogo_info: productMetadata.bogo_info,
    maxPurchaseQuantity: config?.maxPurchaseQuantity,
    min_order_quantity: productMetadata.min_order_quantity,
    is_order_quantity_product_type: productMetadata.is_order_quantity_product_type,
    is_order_quantity_item_type: productMetadata.is_order_quantity_item_type,
    is_store_deal_banner: productMetadata.has_store_deal_banner,
    enableSingleItemSelection: config?.enableSingleItemSelection,
  };

  // 5. 기본 아이템 카드 생성
  const payload = buildItemCard({
    item_cards: itemCards,
    selected_item: foundItem,
    bogo_item_list: bogoItems,
    custom_input_list: customInputList,
    context,
  });

  // 6. 수량 업데이트 (buildItemCard는 수량을 1 또는 최소 수량으로 설정하므로, 요청된 수량으로 덮어씀)
  const lastAddedItem = payload.list[payload.list.length - 1];
  if (lastAddedItem) {
    lastAddedItem.count = quantity;
    payload.total_count = payload.list.reduce((sum, item) => sum + item.count, 0);
  }

  return payload;
};

/**
 * ID와 타입으로 아이템을 찾습니다.
 */
const findItemById = (optionInfo: OptionInfo, targetId: string, optionType: string): ItemListMapValueType | null => {
  const product = optionInfo.catalog_product;

  // 추가 옵션 검색
  if (optionType === OPTION_TYPE.ADDITIONAL) {
    const additionalItems = product.additional_item_list;
    return additionalItems?.find((item) => item.id === targetId) ?? null;
  }

  // 메인 옵션 검색
  const matchedItem = product.matched_item_list.find((item) => item.id === targetId);

  if (matchedItem) return matchedItem;

  // 직진 배송 아이템 검색
  const zigzinItem = product.zigzin_item_list.find((item) => item.id === targetId);
  if (zigzinItem) return zigzinItem;

  return null;
};
